import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api-client";

export type CustomerMembership = {
  id: string;
  status: string;
  propertyId?: string | null;
  membershipTypeName?: string | null;
  endDate?: string | null;
  displayStatus?: string;
  renewedIntoId?: string | null;
};
export type MembershipLoader = (customerId: string) => Promise<CustomerMembership[]>;

// Customer detail returns newest first. Use the same term for the status and
// booking renewal action, rather than offering historical terms individually.
export function currentCustomerMembership(memberships: CustomerMembership[], propertyId?: string) {
  const scoped = memberships.filter(item => propertyId === undefined || item.propertyId === propertyId);
  return scoped.find(item => !item.renewedIntoId && (item.displayStatus || item.status).trim().toLowerCase() === "active") ?? scoped[0];
}

export const loadCustomerMemberships: MembershipLoader = async (customerId) => {
  const response = await apiFetch(`/api/v1/workspace/customers/${encodeURIComponent(customerId)}?jobsPageSize=1`);
  if (!response.ok) throw new Error("Membership status unavailable");
  const payload = await response.json();
  if (!Array.isArray(payload.data?.memberships)) throw new Error("Membership status unavailable");
  return payload.data.memberships;
};

// Scoped to one mounted board/day: duplicate visits and booking share requests.
// Limit background customer-detail requests without delaying the board itself.
export function shareMembershipLookups(load: MembershipLoader): MembershipLoader {
  const cache = new Map<string, Promise<CustomerMembership[]>>();
  const queue: Array<() => void> = [];
  let active = 0;
  const run = () => {
    while (active < 3 && queue.length) queue.shift()!();
  };
  return (customerId) => {
    const existing = cache.get(customerId);
    if (existing) return existing;
    const request = new Promise<CustomerMembership[]>((resolve, reject) => {
      queue.push(() => {
        active++;
        Promise.resolve().then(() => load(customerId)).then(resolve, reject).finally(() => { active--; run(); });
      });
    });
    cache.set(customerId, request);
    void request.catch(() => { cache.delete(customerId); });
    run();
    return request;
  };
}

function statusTag(status: string) {
  const normalized = status.trim().toLowerCase().replace(/[ _-]+/g, " ");
  if (normalized === "active") return { label: "Active member", tone: "bg-emerald-100 text-emerald-900" };
  if (normalized.includes("renew")) return { label: "Membership renewal", tone: "bg-amber-100 text-amber-900" };
  if (["expired", "lapsed"].includes(normalized)) return { label: "Membership expired", tone: "bg-rose-100 text-rose-900" };
  if (["cancelled", "canceled"].includes(normalized)) return { label: "Membership cancelled", tone: "bg-slate-100 text-slate-700" };
  return { label: normalized ? `Membership ${normalized}` : "Membership status unknown", tone: "bg-slate-100 text-slate-700" };
}

export function CustomerMembershipTags({ customerId, propertyId, loadMemberships, compact = false }: {
  customerId: string;
  propertyId?: string;
  loadMemberships?: MembershipLoader;
  compact?: boolean;
}) {
  const [result, setResult] = useState<{ customerId: string; loader: MembershipLoader; memberships?: CustomerMembership[]; failed?: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!loadMemberships) return;
    let stale = false;
    setResult(null);
    void loadMemberships(customerId).then(
      (memberships) => { if (!stale) setResult({ customerId, loader: loadMemberships, memberships }); },
      () => { if (!stale) setResult({ customerId, loader: loadMemberships, failed: true }); },
    );
    return () => { stale = true; };
  }, [customerId, loadMemberships, attempt]);
  if (!loadMemberships) return null;
  const current = result?.customerId === customerId && result.loader === loadMemberships ? result : null;
  if (!current || current.failed) return <span className="text-[10px] font-medium text-[var(--ink-soft)]" title={current?.failed ? "Customer membership status unavailable" : "Loading customer memberships"}>
    {current?.failed ? (compact ? "Member ?" : "Membership status unavailable") : (compact ? "Member …" : "Loading memberships…")}
    {current?.failed && !compact && <button type="button" className="ml-2 underline" onClick={() => setAttempt((value) => value + 1)}>Retry memberships</button>}
  </span>;
  const memberships = (current.memberships ?? []).filter(membership => propertyId === undefined || membership.propertyId === propertyId);
  if (!memberships.length) return compact ? null : <span className="text-xs text-[var(--ink-soft)]">{propertyId ? "No membership at this location" : "No membership"}</span>;
  // The customer API returns newest first. An active plan takes precedence
  // over historical memberships; otherwise show the latest record's status.
  const membership = currentCustomerMembership(memberships)!;
  const { label, tone } = statusTag(membership.status);
  return <span aria-label={propertyId ? "Location membership status" : "Customer membership status"} title={`${membership.membershipTypeName || "Membership"}: ${label}`} className="inline-flex min-w-0 max-w-full">
    <span className={`truncate rounded px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${tone}`}>
      {!compact && membership.membershipTypeName ? `${membership.membershipTypeName} · ` : ""}{label}
    </span>
  </span>;
}
